import Cookies from 'js-cookie';
import type { Brand } from './brand';

const SLATE_THEME_OVERRIDE_COOKIE = 'cvna-slate-theme-override';

interface BrandSignals {
  hostname: string;
  getCookie: (name: string) => string | undefined;
}

const isNonProduction = (): boolean => process.env.NODE_ENV !== 'production';

const isSlateDomain = (hostname: string): boolean => {
  const slateDomain = process.env.SLATE_DOMAIN;
  if (!slateDomain || !hostname) return false;
  return hostname.toLowerCase() === slateDomain.toLowerCase();
};

const resolveBrand = ({ hostname, getCookie }: BrandSignals): Brand => {
  if (isNonProduction() && getCookie(SLATE_THEME_OVERRIDE_COOKIE) === 'true') return 'slate';
  return isSlateDomain(hostname) ? 'slate' : 'carvana';
};

/**
 * No local or shared dev host is the Slate domain, so outside production the vehicle is
 * allowed to move the brand on its own. Without this, reaching Slate off-domain needs
 * either an impersonated session or the dev-panel override, which pins every vehicle in
 * the session to Slate rather than following the one being purchased.
 */
const allowsVehicleBrandUpgrade = (): boolean => isNonProduction();

// Tests stub `location` as a bare object and the axios interceptor runs at module
// load, so neither `location` nor its hostname can be assumed present.
const currentHostname = (): string =>
  typeof location === 'undefined' ? '' : location.hostname ?? '';

/**
 * The only place in the app that reads `location` or cookies for brand. Safe to call at
 * module scope and outside React — the axios interceptor and the error page both do.
 */
const resolveBrandFromEnvironment = (): Brand =>
  resolveBrand({ hostname: currentHostname(), getCookie: name => Cookies.get(name) });

/**
 * Domain only — deliberately blind to the override cookie. For decisions that must not
 * move with the dev-panel theme, like which origin serves the API and token refresh.
 */
const resolveBrandFromDomain = (): Brand =>
  isSlateDomain(currentHostname()) ? 'slate' : 'carvana';

const isBrandOverrideActive = (): boolean =>
  isNonProduction() && Cookies.get(SLATE_THEME_OVERRIDE_COOKIE) === 'true';

export {
  allowsVehicleBrandUpgrade,
  isBrandOverrideActive,
  resolveBrand,
  resolveBrandFromDomain,
  resolveBrandFromEnvironment,
  SLATE_THEME_OVERRIDE_COOKIE
};
export type { BrandSignals };
